import React from 'react';
import { Award, Users, Clock } from 'lucide-react';

interface AboutProps {
  language: string;
}

const About: React.FC<AboutProps> = ({ language }) => {
  const content = {
    en: {
      title: "About BeautyEsthetics",
      description: "Beauty Esthetics has been committed to combining medical expertise with artistic precision, offering advanced aesthetic treatments that help our clients feel confident in their skin.",
      founder: "The clinic was founded by Dr. Abasin, a certified general and emergency doctor with a strong medical background from Denmark. With years of experience treating patients in hospitals, Dr. Abasin recognized the deep connection between health and beauty, and made it his mission to bridge the two through aesthetic medicine.",
      vision: "His vision was to create a space where clients receive safe, personalized, and effective treatments all in a refined, calming environment that inspires trust and reflects true elegance.",
      features: {
        expert: {
          title: "The Expert Team",
          description: "Certified professionals with years of experience"
        },
        care: {
          title: "Personalized Care",
          description: "Treatments tailored to your unique needs"
        },
        approach: {
          title: "Modern Approach",
          description: "Latest techniques and premium products"
        }
      }
    },
    da: {
      title: "Om BeautyEsthetics",
      description: "BeautyEsthetics har forpligtet sig til at kombinere medicinsk ekspertise med kunstnerisk præcision og tilbyder avancerede æstetiske behandlinger, der hjælper vores klienter med at føle sig trygge og selvsikre i deres egen hud.",
      founder: "Klinikken blev grundlagt af Dr. Abasin, en certificeret læge inden for almenmedicin og akutmedicin med en stærk medicinsk baggrund fra Danmark. Med mange års erfaring fra hospitaler erkendte Dr. Abasin den dybe forbindelse mellem sundhed og skønhed og gjorde det til sin mission at forene de to gennem æstetisk medicin.",
      vision: "Hans vision var at skabe et sted, hvor klienter modtager sikre, personlige og effektive behandlinger i rolige og raffinerede omgivelser, der skaber tillid og udstråler ægte elegance.",
      features: {
        expert: {
          title: "Det erfarne team",
          description: "Certificerede fagfolk med mange års erfaring"
        },
        care: {
          title: "Personlig behandling",
          description: "Behandlinger skræddersyet til dine unikke behov"
        },
        approach: {
          title: "Moderne tilgang",
          description: "De nyeste teknikker og førsteklasses produkter"
        }
      }
    }
  };

  return (
    <section className="py-20 bg-[#F5F0EC]" id="about">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 
              className="text-3xl md:text-4xl font-serif font-bold mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {content[language as keyof typeof content].title}
            </h2>
            
            <p className="text-lg mb-6 leading-relaxed">
              {content[language as keyof typeof content].description}
            </p>
            
            <p className="text-lg mb-8 leading-relaxed">
              {content[language as keyof typeof content].founder}
            </p>

            <p className="text-lg mb-8 leading-relaxed">
              {content[language as keyof typeof content].vision}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="group relative p-6 bg-gradient-to-br from-white to-[#FDF8F5] rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_15px_30px_rgb(0,0,0,0.07)] transition-all duration-300 hover:-translate-y-1 hover:scale-[1.02] cursor-pointer overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-white/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <Award className="h-12 w-12 text-[#A68B7C] mb-4 transform group-hover:scale-110 transition-transform duration-300" />
                <h3 
                  className="font-serif text-xl font-semibold mb-2 group-hover:translate-y-[-2px] transition-transform duration-300"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {content[language as keyof typeof content].features.expert.title}
                </h3>
                <p className="text-sm text-gray-600 font-sans group-hover:translate-y-[-2px] transition-transform duration-300">
                  {content[language as keyof typeof content].features.expert.description}
                </p>
              </div>
              
              <div className="group relative p-6 bg-gradient-to-br from-white to-[#FDF8F5] rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_15px_30px_rgb(0,0,0,0.07)] transition-all duration-300 hover:-translate-y-1 hover:scale-[1.02] cursor-pointer overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-white/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <Users className="h-12 w-12 text-[#A68B7C] mb-4 transform group-hover:scale-110 transition-transform duration-300" />
                <h3 
                  className="font-serif text-xl font-semibold mb-2 group-hover:translate-y-[-2px] transition-transform duration-300"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {content[language as keyof typeof content].features.care.title}
                </h3>
                <p className="text-sm text-gray-600 font-sans group-hover:translate-y-[-2px] transition-transform duration-300">
                  {content[language as keyof typeof content].features.care.description}
                </p>
              </div>
              
              <div className="group relative p-6 bg-gradient-to-br from-white to-[#FDF8F5] rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_15px_30px_rgb(0,0,0,0.07)] transition-all duration-300 hover:-translate-y-1 hover:scale-[1.02] cursor-pointer overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-white/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <Clock className="h-12 w-12 text-[#A68B7C] mb-4 transform group-hover:scale-110 transition-transform duration-300" />
                <h3 
                  className="font-serif text-xl font-semibold mb-2 group-hover:translate-y-[-2px] transition-transform duration-300"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {content[language as keyof typeof content].features.approach.title}
                </h3>
                <p className="text-sm text-gray-600 font-sans group-hover:translate-y-[-2px] transition-transform duration-300">
                  {content[language as keyof typeof content].features.approach.description}
                </p>
              </div>
            </div>
          </div>
          
          <div className="relative h-[600px] rounded-lg overflow-hidden shadow-xl">
            <img
              src="https://l0ry9zjg4l.ufs.sh/f/70f42tTcLu8nPKxmywdZx8XrkCtTWfylV3M7BDabYU5jmOvR"
              alt="BeautyEsthetics Clinic Interior"
              className="w-full h-full object-cover lazy-load"
              loading="lazy"
              onLoad={(e) => e.currentTarget.classList.add('loaded')}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;